<template>
	<q-layout-footer>
		<q-toolbar class="mobile-only">
			<q-btn flat round dense @click="showLeft = !showLeft" icon="far fa-bars" />
		</q-toolbar>

		<q-layout-drawer side="left" v-model="showLeft">
			<q-list no-border link inset-separator>
				<q-list-header>{{ getProjectName() }}</q-list-header>

				<q-item v-for="(link, index) in links" :to="{ name: link.to }" :key="index">
					<q-item-side :icon="link.icon" />
					<q-item-main :label="$t(link.text)" />
				</q-item>
			</q-list>

			<q-list no-border link inset-separator>
				<q-list-header>{{ $t('idioms') }}</q-list-header>

				<q-item
					v-for="(language, index) in languages"
					:key="index"
					@click.native="changeIdiom(language.id)"
				>
					<q-item-side>
						<flag :iso="language.country_flag" :squared="false" />
					</q-item-side>
					<q-item-main :label="language.name" />
				</q-item>
			</q-list>

			<copyright />
		</q-layout-drawer>

		<q-toolbar
			class="desktop-only footer-desktop q-py-md"
			:style="$q.platform.is.edge ? 'height: 100px' : ''"
		>
			<div class="row fit">
				<div class="col-12">
					<div class="q-mx-xl">
						<ul>
							<template v-for="(link, index) in links">
								<li :key="'link' + index">
									<router-link :to="{ name: link.to }">{{
										$t(link.text)
									}}</router-link>
								</li>
								<li class="q-mx-sm" :key="'div' + index">|</li>
							</template>

							<template v-for="(language, index) in languages">
								<li
									:key="'flag' + index"
									@click="changeIdiom(language.id)"
									class="cursor-pointer"
								>
									<flag :iso="language.country_flag" :squared="false" />
								</li>
								<li
									class="q-mx-sm"
									:key="'divflag' + index"
									v-if="index != Object.keys(languages).length - 1"
								>
									|
								</li>
							</template>
						</ul>
					</div>
				</div>

				<div class="col-12 text-center q-mt-sm">
					<div class="text-white">
						<small
							>Copyright © {{ new Date().getFullYear() }}: {{ getProjectName() }} v{{
								getVersion()
							}}
							- Powered by <a href="https://take5.com.br">Take 5 LMS</a></small
						>
					</div>
				</div>

				<div class="col-12 text-center">
					<div class="text-white">
						<small>{{
							$t('this_website_is_best_viewed_in_google_chrome_and_mozilla_firefox')
						}}</small>
					</div>
				</div>
			</div>
		</q-toolbar>

		<q-dialog v-model="outdated_app" prevent-close>
			<span slot="title">{{ $t('outdated_app') }}</span>

			<span slot="body">
				{{ $t('your_app_is_outdated_and_have_to_be_updated_to_log_in') }}
			</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="positive" :label="$t('update')" @click="openStore()" />
			</template>
		</q-dialog>
	</q-layout-footer>
</template>

<script>
import Copyright from 'components/Copyright'
import { mapGetters } from 'vuex'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'not_authenticated'] },

	components: {
		copyright: Copyright,
	},

	data() {
		return {
			links: [
				{
					to: 'login',
					text: 'login',
					icon: 'far fa-sign-in',
				},
				{
					to: 'about',
					text: 'about',
					icon: 'far fa-info-square',
				},
				{
					to: 'privacy',
					text: 'privacy_policy',
					icon: 'far fa-user-secret',
				},
				{
					to: 'terms',
					text: 'terms_conditions',
					icon: 'far fa-file-contract',
				},
				{
					to: 'login-help',
					text: 'help',
					icon: 'far fa-question-square',
				},
			],
			showLeft: false,
			outdated_app: false,
		}
	},

	computed: {
		...mapGetters({
			id_user_language_selected: 'common/id_user_language_selected',
			app_version: 'params/login_app_version',
			languages: 'params/languages',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
				},
			})
		},
	},

	methods: {
		changeIdiom(code) {
			if (!code) return
			this.showLeft = false
			this.$store.dispatch('common/SET_LANGUAGE', [code])
		},

		getParams() {
			this.$axios
				.get('/clients/config/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('params/SET_PARAMS', [
						response.data.login,
						response.data.clients,
						response.data.register,
						response.data.languages,
						response.data.qty_login_attempts,
						response.data.user_profile,
					])
					this.$store.dispatch('params/SET_PARAMS_LOADED', [true])

					const nav_language = navigator.language.split('-')[0]
					if (nav_language) {
						const language = response.data.languages.find(
							(l) => l.code.split('-')[0] === nav_language
						)

						if (language) this.$store.dispatch('common/SET_LANGUAGE', [language.id])
						else {
							this.$store.dispatch('common/SET_LANGUAGE', [
								response.data.login.default_language_id,
							])
						}
					} else {
						this.$store.dispatch('common/SET_LANGUAGE', [
							response.data.login.default_language_id,
						])
					}

					this.checkAppVersion()
				})
				.catch((e) => {
					console.log(e)
				})
		},

		checkAppVersion() {
			if (this.$q.platform.is.cordova && this.app_version) {
				let app_version = process.env.APP_VERSION.split('.')
				let api_version = this.app_version.split('.')

				if (app_version[1] != api_version[1]) {
					this.outdated_app = true
				}
			}
		},

		openStore() {
			if (this.$q.platform.is.android && this.$q.platform.is.cordova) {
				window.open(`market://details?id=${process.env.CORDOVA_ID}`)
			}
		},

		getProjectName() {
			return process.env.PROJECT_NAME
		},

		getVersion() {
			return process.env.APP_VERSION
		},
	},

	watch: {
		id_user_language_selected: function(val) {
			let language = this.languages.find((l) => l.id === this.id_user_language_selected)

			if (language) this.$i18n.i18next.changeLanguage(language.code.split('-')[0])
		},
	},

	created() {
		this.getParams()
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

ul
    list-style-type none
    margin 0
    padding 0
    overflow hidden
    color $secondary

li
    float left
    & a
        display block
        text-align center
</style>
